import { formatDate, formatStaffName } from "@/lib/format-data";
import { renderAnnotatedImage } from "@/lib/patient-export/render-annotated-image";
import { escapeHtml } from "@/lib/print-utils";

import { Annotation } from "@/types/annotation";
import { NoteAttachment } from "@/types/notes";

import { getSignedUrlBase64 } from "@/api/auth/gcs";

// Cap concurrent byte fetches + canvas renders so a note with a dozen phone
// photos doesn't hold several full-resolution bitmaps in memory at once.
const RENDER_CONCURRENCY = 4;

// Long-edge cap for the embedded copy. Photos print two per row at ~3.25in, so
// 1600px still lands around 490 DPI, while the untouched bytes of a modern
// phone photo would bloat the document string handed to the print window.
const MAX_EDGE_PX = 1600;
const JPEG_QUALITY = 0.85;

// Attachments carry no mime column, so image-ness is inferred from the stored
// path / original filename the same way the patient export does. TIFF is
// deliberately absent: the backend returns it as `data:image/png` with raw TIFF
// bytes and no browser decodes it, so counting one would promise a photo that
// can only ever render as a placeholder.
const IMAGE_EXTENSIONS = new Set([
  ".avif",
  ".bmp",
  ".gif",
  ".heic",
  ".heif",
  ".jpeg",
  ".jpg",
  ".png",
  ".webp",
]);

/**
 * The note attachments that can appear in a printout: images whose bytes we
 * can resolve. Revision dedupe and upload-status filtering already happen
 * server-side, so the list needs no further pruning.
 */
export function getPrintablePhotos(
  attachments: NoteAttachment[] | null | undefined,
): NoteAttachment[] {
  return (attachments ?? []).filter(
    (attachment) => !!attachment.gcsPath && isImageAttachment(attachment),
  );
}

/**
 * Build the "Photos" section of a printed note: each photo baked with the
 * annotations the provider drew, downscaled for print, and captioned with the
 * audit trail (name · date · uploader).
 *
 * Returns raw HTML rather than markdown — a two-up captioned grid isn't
 * expressible in markdown, and react-markdown doesn't render raw HTML.
 */
export async function buildNotePhotosHtml(
  attachments: NoteAttachment[] | null | undefined,
): Promise<{ html: string; failed: number }> {
  const photos = getPrintablePhotos(attachments);
  if (photos.length === 0) return { html: "", failed: 0 };

  const figures: string[] = new Array(photos.length).fill("");
  let failed = 0;
  await mapWithConcurrency(photos, RENDER_CONCURRENCY, async (photo, index) => {
    const figure = await renderPhotoFigure(photo);
    figures[index] = figure.html;
    if (!figure.ok) failed += 1;
  });

  return {
    html: `
    <section class="note-photos">
      <h2 class="note-photos-title">Photos</h2>
      <div class="note-photos-grid">${figures.join("")}</div>
    </section>`,
    failed,
  };
}

async function renderPhotoFigure(
  photo: NoteAttachment,
): Promise<{ html: string; ok: boolean }> {
  const caption = escapeHtml(buildCaption(photo));
  try {
    const dataUrl = await loadPhotoDataUrl(photo);
    return {
      html: `<figure class="note-photo">
      <img src="${dataUrl}" alt="${caption}" />
      <figcaption>${caption}</figcaption>
    </figure>`,
      ok: true,
    };
  } catch (error) {
    // An audit copy should say a photo is missing rather than silently drop it.
    console.error(
      `Failed to render note photo ${photo.id} (${photo.gcsPath}):`,
      error,
    );
    return {
      html: `<figure class="note-photo">
      <div class="note-photo-missing">Photo could not be included in this printout.</div>
      <figcaption>${caption}</figcaption>
    </figure>`,
      ok: false,
    };
  }
}

async function loadPhotoDataUrl(photo: NoteAttachment): Promise<string> {
  // Pull the bytes through the backend as a data URI: the bucket isn't
  // CORS-enabled for the app origin, and the backend transcodes HEIC to JPEG
  // so iPhone photos decode in the browser at all.
  const signed = await getSignedUrlBase64(photo.gcsPath as string);
  const source = signed?.signedUrl;
  if (!source?.startsWith("data:image/")) {
    throw new Error("Photo bytes unavailable");
  }

  const original = await (await fetch(source)).blob();
  const annotations = photo.annotations as Annotation[] | null | undefined;
  const isAnnotated = !!annotations && annotations.length > 0;
  if (!isAnnotated && !(await exceedsMaxEdge(original))) {
    // Already small enough and nothing to draw on it: re-encoding here would
    // only put JPEG ringing on text and hairlines in scanned forms, for no
    // reduction in size.
    return source;
  }

  const baked = isAnnotated
    ? await renderAnnotatedImage(original, annotations)
    : original;

  return await downscaleToJpegDataUrl(baked);
}

async function exceedsMaxEdge(blob: Blob): Promise<boolean> {
  const bitmap = await createImageBitmap(blob);
  try {
    return Math.max(bitmap.width, bitmap.height) > MAX_EDGE_PX;
  } finally {
    bitmap.close?.();
  }
}

async function downscaleToJpegDataUrl(blob: Blob): Promise<string> {
  const bitmap = await createImageBitmap(blob);
  const scale = Math.min(
    1,
    MAX_EDGE_PX / Math.max(bitmap.width, bitmap.height),
  );
  const width = Math.max(1, Math.round(bitmap.width * scale));
  const height = Math.max(1, Math.round(bitmap.height * scale));

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;

  try {
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("2D canvas context unavailable");
    // JPEG carries no alpha, and renderAnnotatedImage always returns a PNG:
    // without an opaque ground a transparent source encodes to black.
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, width, height);
    ctx.drawImage(bitmap, 0, 0, width, height);
  } finally {
    bitmap.close?.();
  }

  return canvas.toDataURL("image/jpeg", JPEG_QUALITY);
}

function buildCaption(photo: NoteAttachment): string {
  return [
    photo.name?.trim() || photo.originalFilename?.trim() || "Photo",
    photo.createdDate ? formatDate(photo.createdDate) : "",
    photo.provider ? formatStaffName(photo.provider) : "",
  ]
    .filter(Boolean)
    .join(" · ");
}

function isImageAttachment(photo: NoteAttachment): boolean {
  return [photo.gcsPath, photo.originalFilename].some((candidate) => {
    const tail = (candidate ?? "").split("/").pop() ?? "";
    const dot = tail.lastIndexOf(".");
    return dot > 0 && IMAGE_EXTENSIONS.has(tail.slice(dot).toLowerCase());
  });
}

async function mapWithConcurrency<T>(
  items: T[],
  concurrency: number,
  worker: (item: T, index: number) => Promise<void>,
): Promise<void> {
  let cursor = 0;
  const runners = Array.from(
    { length: Math.min(concurrency, items.length) },
    async () => {
      while (cursor < items.length) {
        const index = cursor;
        cursor += 1;
        await worker(items[index], index);
      }
    },
  );
  await Promise.all(runners);
}
